'use client';

import Link from '@eliadmin/core/src/components/link';
import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@eliadmin/core/src/components/ui/popover';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { ArrowLeft, ArrowRight, ChevronDown, ChevronRight, Globe2, MapPin, X } from 'lucide-react';
import { LuPlaneTakeoff } from 'react-icons/lu';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from '../ui/sheet';

const categoryHref = (category: CategoriesEntity) =>
  '/' + category.slug!.replace(/^\/+/, '') + '?type=aktualne';

function DestinationTiles({
  categories,
  onNavigate,
  mobile = false,
}: {
  categories: CategoriesEntity[];
  onNavigate: () => void;
  mobile?: boolean;
}) {
  return (
    <ul
      className={cn(
        'ea-offers-menu__destinations grid',
        mobile ? 'grid-cols-1 gap-2' : 'grid-cols-4 gap-3',
      )}
    >
      {categories.map(category => (
        <li key={category.id} className="min-w-0">
          <Link
            prefetch={false}
            href={categoryHref(category)}
            onClick={onNavigate}
            className={cn(
              'flex h-full items-center gap-3 border border-border bg-white px-4 text-sm font-medium transition-colors hover:bg-[#F6F6F6] focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary',
              mobile ? 'min-h-[56px] rounded-[12px] py-3' : 'min-h-[88px] rounded-[16px] py-4',
            )}
          >
            <MapPin size={20} aria-hidden="true" className="shrink-0 text-primary" />
            <span className={cn('min-w-0 flex-1', mobile ? 'truncate' : 'break-words')}>
              {category.name}
            </span>
            <ArrowRight size={16} aria-hidden="true" className="shrink-0 text-primary" />
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function HeaderOffers({
  allCategories,
  mobile = false,
}: {
  allCategories: CategoriesEntity[];
  mobile?: boolean;
}) {
  // Categories with tours do not necessarily have child categories.
  const categories = allCategories.filter(category => category.slug?.trim());
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | string | null>(null);
  const [menuTop, setMenuTop] = useState(0);
  const [menuWidth, setMenuWidth] = useState<number>();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const selected =
    categories.find(category => category.id === selectedId) ?? (mobile ? undefined : categories[0]);
  const children = selected?.childs?.filter(category => category.slug?.trim()) ?? [];
  const cs = locale === 'cs';
  const copy = {
    title: cs ? 'Nabídka zájezdů' : 'Ponuka zájazdov',
    all: cs ? 'Všechny zájezdy' : 'Všetky zájazdy',
    categories: cs ? 'Všechny kategorie' : 'Všetky kategórie',
    category: cs ? 'Zájezdy v této kategorii' : 'Zájazdy v tejto kategórii',
    destinations: cs ? 'Vyberte destinaci' : 'Vyberte destináciu',
    description: cs
      ? 'Vyberte si kategorii nebo destinaci.'
      : 'Vyberte si kategóriu alebo destináciu.',
    view: cs ? 'Zobrazit nabídku' : 'Zobraziť ponuku',
    close: cs ? 'Zavřít nabídku zájezdů' : 'Zavrieť ponuku zájazdov',
  };

  const measureMenu = useCallback(() => {
    const trigger = triggerRef.current;
    setMenuWidth(trigger?.closest('[data-offers-menu-row]')?.getBoundingClientRect().width);
    setMenuTop(trigger?.closest('header')?.getBoundingClientRect().bottom ?? 0);
  }, []);

  useEffect(() => {
    if (!open || mobile) return;
    const header = triggerRef.current?.closest('header');
    if (!header) return;
    const observer = new ResizeObserver(measureMenu);
    observer.observe(header);
    return () => observer.disconnect();
  }, [open, mobile, measureMenu]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [selectedId]);

  useEffect(() => {
    if (!open) return;
    const desktop = window.matchMedia('(min-width: 1280px)');
    const closeOnBreakpointChange = () => {
      if (mobile === desktop.matches) setOpen(false);
    };
    desktop.addEventListener('change', closeOnBreakpointChange);
    return () => desktop.removeEventListener('change', closeOnBreakpointChange);
  }, [mobile, open]);

  function changeOpen(nextOpen: boolean) {
    if (nextOpen) {
      if (mobile) setSelectedId(null);
      else measureMenu();
    }
    setOpen(nextOpen);
  }

  const close = () => changeOpen(false);
  const closeButton = (
    <button
      type="button"
      aria-label={copy.close}
      onClick={close}
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] text-primary hover:bg-[#F6F6F6] focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
    >
      <X size={20} aria-hidden="true" />
    </button>
  );
  const footer = (
    <div
      className={cn(
        'ea-offers-menu__footer shrink-0 border-t border-border bg-white',
        mobile ? 'px-5 pb-[max(16px,env(safe-area-inset-bottom))] pt-4' : 'px-6 py-4',
      )}
    >
      <Button asChild variant={mobile ? 'default' : 'outline'} className="h-12 w-full">
        <Link prefetch={false} href={ROUTES.TOURS(locale) + '?type=aktualne'} onClick={close}>
          {copy.all}
          <ArrowRight size={18} aria-hidden="true" />
        </Link>
      </Button>
    </div>
  );
  const destinations = selected && (
    <section aria-label={selected.name}>
      {mobile && (
        <button
          type="button"
          onClick={() => setSelectedId(null)}
          className="mb-5 flex min-h-11 items-center gap-2 text-sm font-medium text-primary"
        >
          <ArrowLeft size={18} aria-hidden="true" />
          {copy.categories}
        </button>
      )}
      <div
        className={cn(
          'mb-5 flex gap-4',
          mobile ? 'flex-col items-start gap-2' : 'items-center justify-between',
        )}
      >
        <h2 className="text-2xl font-semibold">{selected.name}</h2>
        <Link
          prefetch={false}
          href={categoryHref(selected)}
          onClick={close}
          className="flex min-h-11 items-center gap-2 text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          {children.length > 0 ? copy.category : copy.view}
          <ArrowRight size={16} aria-hidden="true" className="shrink-0" />
        </Link>
      </div>
      {children.length > 0 && <p className="mb-3 text-sm">{copy.destinations}</p>}
      <DestinationTiles
        categories={children.length > 0 ? children : [selected]}
        onNavigate={close}
        mobile={mobile}
      />
    </section>
  );

  if (mobile) {
    return (
      <Sheet open={open} onOpenChange={changeOpen}>
        <SheetTrigger asChild>
          <Button className="h-12 w-full justify-between rounded-[12px] px-4">
            <span className="flex items-center gap-3">
              <LuPlaneTakeoff size={20} aria-hidden="true" />
              {copy.title}
            </span>
            <ChevronDown size={18} aria-hidden="true" />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="bottom"
          showCloseIcon={false}
          overlayClassName="z-[1001]"
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          className="ea-offers-menu ea-offers-menu--mobile z-[1002] mx-auto flex h-[min(720px,90dvh)] w-full max-w-[720px] flex-col gap-0 overflow-hidden rounded-t-[24px] bg-white p-0 text-foreground"
        >
          <div className="ea-offers-menu__header flex shrink-0 items-center justify-between gap-3 border-b border-border px-5 py-4">
            <div className="min-w-0">
              <SheetTitle id={titleId} className="text-xl">
                {copy.title}
              </SheetTitle>
              <SheetDescription id={descriptionId} className="mt-1 text-sm">
                {copy.description}
              </SheetDescription>
            </div>
            {closeButton}
          </div>
          <div
            ref={scrollRef}
            tabIndex={0}
            role="region"
            aria-label={copy.destinations}
            className="ea-offers-menu__scroll min-h-0 flex-1 overflow-y-auto overscroll-contain bg-[#F6F6F6] p-5"
          >
            {selected ? (
              destinations
            ) : (
              <ul className="grid grid-cols-1 gap-2">
                {categories.map(category => {
                  const contents = (
                    <>
                      <Globe2 size={24} aria-hidden="true" className="shrink-0 text-primary" />
                      <span className="min-w-0 flex-1 truncate">{category.name}</span>
                      <ChevronRight
                        size={16}
                        aria-hidden="true"
                        className="shrink-0 text-primary"
                      />
                    </>
                  );
                  const className =
                    'flex min-h-[56px] w-full items-center gap-3 rounded-[12px] border border-border bg-white px-4 py-3 text-left text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary';
                  return (
                    <li key={category.id} className="min-w-0">
                      {category.childs?.length ? (
                        <button
                          type="button"
                          className={className}
                          onClick={() => setSelectedId(category.id)}
                        >
                          {contents}
                        </button>
                      ) : (
                        <Link
                          prefetch={false}
                          href={categoryHref(category)}
                          onClick={close}
                          className={className}
                        >
                          {contents}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {footer}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <Button ref={triggerRef} className="whitespace-nowrap">
          {open ? (
            <X size={20} aria-hidden="true" />
          ) : (
            <LuPlaneTakeoff size={20} aria-hidden="true" />
          )}
          {t('text3240')}
          <ChevronDown
            size={18}
            aria-hidden="true"
            className={cn('transition-transform', open && 'rotate-180')}
          />
        </Button>
      </PopoverTrigger>
      {open &&
        createPortal(
          <div
            aria-hidden="true"
            className="fixed inset-x-0 bottom-0 z-[37] bg-black/50"
            style={{ top: menuTop }}
          />,
          document.body,
        )}
      <PopoverContent
        align="start"
        sideOffset={12}
        collisionPadding={16}
        aria-labelledby={titleId}
        className="ea-offers-menu z-[70] flex w-[calc(100vw-32px)] max-w-[calc(100vw-32px)] overflow-hidden rounded-[16px] border border-border bg-white p-0 text-foreground shadow-lg"
        style={{ width: menuWidth, height: 'min(620px,calc(100dvh - ' + (menuTop + 24) + 'px))' }}
      >
        <nav
          aria-label={t('text3240')}
          className="w-[320px] shrink-0 overflow-y-auto overscroll-contain bg-[#F6F6F6] p-4"
        >
          <ul className="space-y-1">
            {categories.map(category => (
              <li key={category.id}>
                <button
                  type="button"
                  aria-pressed={selected?.id === category.id}
                  onClick={() => setSelectedId(category.id)}
                  onPointerEnter={event => {
                    if (event.pointerType === 'mouse') setSelectedId(category.id);
                  }}
                  className={cn(
                    'flex min-h-11 w-full items-center gap-3 rounded-[12px] px-3 py-3 text-left text-sm font-medium transition-colors hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary',
                    selected?.id === category.id && 'bg-white text-primary',
                  )}
                >
                  <Globe2 size={18} aria-hidden="true" className="shrink-0 text-primary" />
                  <span className="min-w-0 flex-1 break-words">{category.name}</span>
                  <ChevronRight size={16} aria-hidden="true" className="shrink-0 text-primary" />
                </button>
              </li>
            ))}
          </ul>
        </nav>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="ea-offers-menu__header flex shrink-0 items-center justify-between gap-4 border-b border-border px-6 py-4">
            <p id={titleId} className="text-xl font-semibold">
              {t('text3240')}
            </p>
            {closeButton}
          </div>
          <div
            ref={scrollRef}
            tabIndex={0}
            role="region"
            aria-label={copy.destinations}
            className="ea-offers-menu__scroll min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-5"
          >
            {destinations}
          </div>
          {footer}
        </div>
      </PopoverContent>
    </Popover>
  );
}
